// Iter-196.W (C6 from audit) — Freshness timestamp surfaced on the
// CompareDrawerModal header + on ComparisonPage hero so visitors
// see that pricing and editorial verdicts are kept current.
//
// Two timestamps surface:
//   • Pricing — date of last PartnerRateTable refresh (build-time
//     stamp from `process.env.REACT_APP_BUILD_TIME` if present,
//     falls back to "today" since the rate cards on CarSavr are
//     edited live during deploys).
//   • Verdict — date of last editorial review (passed via prop
//     from the ComparisonPage / hard-coded to the last sprint
//     review date on the modal).
//
// Format: "Updated 2 hr ago · Verdict reviewed Feb 16, 2026"
// Compact, all-lowercase, no jargon.
import { useState } from "react";
import { Clock } from "lucide-react";

// Build-time stamp injected by CRA from REACT_APP_BUILD_TIME if set.
// In dev/preview we fall back to the current time so the relative
// label always reads "just now" instead of an undefined / "NaN ago".
const BUILD_TIME_ISO =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_BUILD_TIME) ||
  null;

/**
 * Convert an ISO timestamp into a short relative label.
 * "just now" / "5 min ago" / "2 hr ago" / "3 days ago" / "Feb 14".
 */
export function relativeStamp(iso, now = Date.now()) {
  if (!iso) return "just now";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "just now";
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  // For older stamps, show absolute date — relative units beyond a
  // week feel imprecise. "Feb 14" is more honest than "2 wk ago".
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Stamp component. `pricingIso` is the data freshness; `verdictIso`
 * is the editor-review date. Both default to the build-time stamp.
 *
 * Variant `compact` is for inline use inside the CompareDrawerModal
 * header (one row, tight padding). Variant `inline` is for use on
 * `ComparisonPage` hero where it sits next to the byline.
 */
export default function CompareFreshnessStamp({
  pricingIso = BUILD_TIME_ISO,
  verdictIso = BUILD_TIME_ISO,
  variant = "compact",
  testidSlug = "default",
}) {
  // Lazy initializer keeps the timestamp pure across re-renders + lets
  // react-hooks/purity stay green (Date.now() in a useState initializer
  // is the canonical pattern for "captured-on-mount" values).
  const [now] = useState(() => Date.now());
  const pricingLabel = relativeStamp(pricingIso, now);
  const verdictLabel = verdictIso
    ? relativeStamp(verdictIso, now)
    : pricingLabel;
  const wrapCls =
    variant === "inline"
      ? "inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500"
      : "inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500";
  return (
    <span
      data-testid={`compare-freshness-stamp-${testidSlug}`}
      className={wrapCls}
      title={`Pricing refreshed ${pricingLabel}; editor verdict reviewed ${verdictLabel}`}
    >
      <Clock className="w-3 h-3 shrink-0" strokeWidth={2.25} />
      <span data-testid={`compare-freshness-pricing-${testidSlug}`}>
        Updated {pricingLabel}
      </span>
      <span className="text-slate-300">·</span>
      <span data-testid={`compare-freshness-verdict-${testidSlug}`}>
        Verdict reviewed {verdictLabel}
      </span>
    </span>
  );
}
